import { TextField, IconButton, Avatar, Checkbox, Autocomplete } from "@mui/material";
import { Clear } from "@mui/icons-material";
import {
  useEffect,
  useState,
  Children,
  forwardRef,
  useRef,
  useMemo,
  useImperativeHandle,
} from "react";
import { debounce } from "lodash";
import { FixedSizeList, ListChildComponentProps } from "react-window";
import AutoSizer from "react-virtualized-auto-sizer";
import { ICellEditorParams } from "ag-grid-community";
import { ICellEditorReactComp } from "ag-grid-react";
import { useCallingEpsm, useCallingPsm } from "../hooks";
import { CallingPsmMetadata } from "types/portfolio";
import { ListboxProps } from "types/ListboxComponent.types";

interface SearchOption {
  name: string;
  value: string;
  subText?: string;
  active: boolean;
}

let lastQuery: any;

const CallingEpsmCellEditor = forwardRef<
  ICellEditorReactComp,
  ICellEditorParams & { type: string }
>(({ value: initValue, type }, ref) => {
  const [inputValue, setInputValue] = useState("");
  const [query, setQuery] = useState("");
  const [value, setValue] = useState<SearchOption[]>([]);
  const [options, setOptions] = useState<SearchOption[]>([]);
  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<typeof TextField>();

  useImperativeHandle(ref, () => ({
    isPopup: () => true,
    getValue: () => (value.length ? value.map((x) => x.name).join(", ") : null),
  }));

  const { data: epsmList, error: epsmError } = useCallingEpsm();
  const { data: psmList, error: psmError } = useCallingPsm();

  let users: CallingPsmMetadata[] | undefined = [];
  let error: Partial<Error> = {};

  if (type === "epsm") {
    users = epsmList;
    error = epsmError;
  } else {
    users = psmList;
    error = psmError;
  }

  const generateOption = (users?: { cecId: string; name: string }[]): SearchOption[] => {
    return (
      users?.map((x) => ({
        name: x.name,
        value: x.cecId,
        subText: x.cecId,
        active: true,
      })) ?? []
    );
  };

  const ops = useMemo(() => generateOption(users), [users]);

  useEffect(() => {
    setOptions(ops);
    if (ops.length && initValue) {
      const valueArray: string[] = initValue?.split(", ") ?? [];
      const selected = ops.filter((x) => valueArray.includes(x.name));
      if (selected) setValue(selected);
    }
  }, [ops, initValue]);

  const onChange = (_e: any, values: SearchOption[]) => setValue(values);

  const onInputChange = (_e: any, input: string, reason: string) => {
    if (reason === "reset") return;
    setInputValue(input);
  };

  useEffect(() => {
    const debouncedSetQuery = debounce(() => setQuery(inputValue), 500);
    if (lastQuery && lastQuery.cancel) lastQuery.cancel();
    lastQuery = debouncedSetQuery;
    debouncedSetQuery();
  }, [inputValue]);

  const clear = () => setInputValue("");

  useEffect(() => {
    // @ts-ignore
    setTimeout(() => inputRef?.current?.focus());
  }, []);

  const ListboxComponent = forwardRef<HTMLDivElement, ListboxProps>(({ children }, ref) => {
    const itemData = Children.toArray(children);
    const rowRender = ({ index, style }: ListChildComponentProps) => (
      <div style={style}>{itemData[index]}</div>
    );

    return (
      <div
        style={{ height: 290 }}
        ref={ref}
        id="abc123"
      >
        <AutoSizer>
          {({ width, height }) => (
            <FixedSizeList
              width={width}
              height={height}
              itemCount={itemData.length}
              itemSize={42}
              itemData={itemData}
            >
              {rowRender}
            </FixedSizeList>
          )}
        </AutoSizer>
      </div>
    );
  });

  if (error)
    return (
      <span style={{ padding: 8, fontSize: 14 }}>
        Failed to load CallingEpsm/CallingPsm options
      </span>
    );

  return (
    <div style={{ display: "flex", alignItems: "center" }}>
      <Autocomplete
        multiple
        options={options}
        getOptionLabel={(option) => option.name}
        isOptionEqualToValue={(option, value) => option.value === value.value}
        size="small"
        limitTags={2}
        clearOnBlur={false}
        blurOnSelect={false}
        value={value}
        inputValue={inputValue}
        onChange={onChange}
        onInputChange={onInputChange}
        open
        noOptionsText="No CallingEpsm found"
        loadingText="Please wait while we are fetching"
        loading={users === undefined}
        filterOptions={(options) =>
          options.filter(
            (o) =>
              !!!query ||
              o.name?.toLowerCase().includes(query.toLowerCase()) ||
              o.value?.toLowerCase().includes(query.toLowerCase())
          )
        }
        ListboxComponent={ListboxComponent as any}
        ListboxProps={{ ref: rootRef }}
        renderInput={(params) => (
          <TextField
            {...params}
            inputRef={inputRef}
            variant="outlined"
            placeholder="CEC or Name"
            sx={{
              width: 360,
              height: 45,
              "& fieldset": { borderRadius: "5px" },
            }}
            InputProps={{
              ...params.InputProps,
              style: { height: 45, width: 360 },
              endAdornment: inputValue ? (
                <IconButton
                  size="small"
                  onClick={clear}
                >
                  <Clear fontSize="small" />
                </IconButton>
              ) : null,
            }}
          />
        )}
        renderOption={(props, option, { inputValue, selected }) => {
          const regex = new RegExp(inputValue.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"), "ig");
          const spanText = option.name.replace(
            regex,
            `<span style="background-color:#FFE399">$&</span>`
          );
          const subSpanText = option.subText
            ? option.subText.replace(regex, `<span style="background-color:#FFE399">$&</span>`)
            : "";

          return (
            <li {...props}>
              <div style={{ display: "flex", alignItems: "center" }}>
                <Checkbox
                  color="primary"
                  style={{ paddingTop: 0 }}
                  checked={selected}
                />
                <Avatar
                  src={
                    option.value
                      ? `https://wwwin.cisco.com/dir/photo/zoom/${option.value}.jpg`
                      : undefined
                  }
                  sx={{ width: 32, height: 32, mr: 1 }}
                >
                  {option.name?.[0]}
                </Avatar>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-start",
                  }}
                >
                  <div
                    style={{
                      width: 320,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                    dangerouslySetInnerHTML={{ __html: spanText }}
                  />
                  {subSpanText && (
                    <div
                      style={{ color: "#666", fontSize: 12 }}
                      dangerouslySetInnerHTML={{ __html: subSpanText }}
                    />
                  )}
                </div>
              </div>
            </li>
          );
        }}
      />
    </div>
  );
});

export default CallingEpsmCellEditor;
